import React from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import type { SituacaoBaseComercial } from '../api';
import { SITUACAO_LABEL } from '../utils/format';

/**
 * Cores por situação (Seção 7 da spec).
 *
 * O agrupamento visual segue o que o usuário precisa distinguir de
 * relance: cinza = rascunho/trabalho em curso, âmbar = aguardando
 * decisão, verde = valendo, vermelho = fora de uso.
 */
const SITUACAO_CLASSES: Record<SituacaoBaseComercial, string> = {
  em_elaboracao: 'bg-gray-100 text-gray-700 ring-gray-200',
  em_revisao: 'bg-gray-100 text-gray-700 ring-gray-200',
  // Amber-100/amber-800 estava com contraste borderline (WCAG AA falha
  // em backgrounds claros); troca para amber-900 no texto garante ≥ 7:1.
  em_aprovacao: 'bg-amber-100 text-amber-900 ring-amber-300',
  aprovada: 'bg-sky-100 text-sky-800 ring-sky-200',
  publicada: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  vigente: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  suspensa: 'bg-orange-100 text-orange-800 ring-orange-200',
  expirada: 'bg-red-100 text-red-800 ring-red-200',
  cancelada: 'bg-red-100 text-red-800 ring-red-200',
  // "Substituída" era slate/cinza puro — visualmente igual ao rascunho.
  // Muda para violeta médio: sinaliza estado terminal sem confundir com
  // Cancelada (vermelho) nem com Em Elaboração (cinza).
  substituida: 'bg-violet-100 text-violet-800 ring-violet-200',
};

export function SituacaoBadge({
  situacao,
}: {
  situacao: SituacaoBaseComercial;
}): React.ReactElement {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${SITUACAO_CLASSES[situacao]}`}
    >
      {SITUACAO_LABEL[situacao]}
    </span>
  );
}

/**
 * Reajuste devido sem taxa publicada (design §D9). É pendência
 * acionável, não erro — por isso âmbar, e com título explicando o que
 * destrava.
 */
export function ReajustePendenteBadge(): React.ReactElement {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
      title="Reajuste devido, aguardando a publicação da taxa do índice para a competência."
    >
      <AlertTriangle className="h-3 w-3" />
      Reajuste pendente
    </span>
  );
}

/** Versão somente leitura — RN-COM-006. */
export function SomenteLeituraBadge(): React.ReactElement {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
      title="Versão publicada é somente leitura. Alterar condições gera uma nova versão (RN-COM-005)."
    >
      <Lock className="h-3 w-3" />
      Somente leitura
    </span>
  );
}

/** `BC-2026-000145 v3` — código do grupo + versão. */
export function CodigoVersaoBadge({
  codigo,
  versao,
  vigente,
}: {
  codigo: string;
  versao: number;
  vigente?: boolean;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-600">
      {codigo}
      <span
        className={`rounded px-1 ${vigente ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}
      >
        v{versao}
      </span>
    </span>
  );
}
